import React, { useState, useEffect } from "react";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import {
  Typography,
  Box,
  Paper,
  Stack,
  Chip,
  Divider,
  Avatar,
  Tooltip,
  useMediaQuery,
  useTheme,
  alpha,
  Slide,
} from "@mui/material";
import EventNoteIcon from "@mui/icons-material/EventNote";
import PersonIcon from "@mui/icons-material/Person";

const TYPE_COLORS = {
  EVENT: "primary.main",
  ERROR: "error.main",
  SUCCESS: "success.main",
  WARNING: "warning.main",
  INFO: "info.main",
};

function formatTimestamp(ts) {
  if (!ts?.seconds) return "N/A";
  return new Date(ts.seconds * 1000).toLocaleString("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function getTypeColor(type, theme) {
  const muiColor = TYPE_COLORS[type?.toUpperCase()] || "primary.main";
  const [key, shade] = muiColor.split(".");
  return theme.palette[key][shade];
}

function ManageLogs() {
  const [logs, setLogs] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const darkMode = theme.palette.mode === "dark";

  useEffect(() => {
    const logsQuery = query(
      collection(db, "logs"),
      orderBy("timestamp", "desc")
    );
    const unsubscribe = onSnapshot(logsQuery, (snapshot) => {
      const logsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setLogs(logsData);
    });
    return () => unsubscribe();
  }, []);

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        minHeight: "100vh",
        background: "#000",
        transition: "background 0.4s",
      }}
    >
      <Typography
        variant={isMobile ? "h5" : "h4"}
        fontWeight={800}
        mb={4}
        color={"#CDD7E1"}
        letterSpacing={-1}
      >
        Activity Logs
      </Typography>
      {logs.length === 0 ? (
        <Typography color="text.secondary">No logs available.</Typography>
      ) : (
        <Stack spacing={3}>
          {logs.map((log, idx) => {
            const type = log.type?.toUpperCase() || "EVENT";
            const color = getTypeColor(type, theme);

            return (
              <Slide in direction="up" timeout={300 + idx * 80} key={log.id}>
                <Paper
                  elevation={0}
                  sx={{
                    display: "flex",
                    gap: 2,
                    borderRadius: 3,
                    p: { xs: 2, sm: 3 },
                    bgcolor: "#0B0D0E",
                    borderLeft: `6px solid #171A1C`,
                    boxShadow: darkMode
                      ? "0 4px 24px 0 rgba(25, 118, 210, 0.10)"
                      : "0 2px 16px 0 rgba(25,118,210,0.08)",
                    alignItems: "stretch",
                    backdropFilter: darkMode ? "blur(4px)" : undefined,
                    overflow: "hidden",
                    transition: "background 0.4s, box-shadow 0.3s",
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: "0B6BCB",
                      width: 48,
                      height: 48,
                      mt: { xs: 0, sm: 0.5 },
                      boxShadow: darkMode
                        ? "0 2px 8px 0 rgba(20,100,200,0.11)"
                        : "0 2px 8px 0 rgba(0,0,0,0.09)",
                    }}
                  >
                    <EventNoteIcon />
                  </Avatar>
                  <Box flex={1} minWidth={0}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      mb={1}
                    >
                      <Chip
                        label={type}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          letterSpacing: "1px",
                          bgcolor: color,
                          color: "#fff",
                          fontSize: 13,
                          boxShadow: "0 2px 6px 0 rgba(25,118,210,0.10)",
                        }}
                      />
                      <Tooltip title={formatTimestamp(log.timestamp)}>
                        <Typography
                          variant="body2"
                          color={theme.palette.text.secondary}
                          sx={{
                            fontWeight: 600,
                            letterSpacing: 0.5,
                            ml: 1,
                          }}
                        >
                          {formatTimestamp(log.timestamp)}
                        </Typography>
                      </Tooltip>
                      <Box
                        sx={{
                          ml: "auto",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <PersonIcon sx={{ fontSize: 20, color, mr: 0.5 }} />
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color={theme.palette.text.primary}
                        >
                          {log.admin || "Unknown Admin"}
                        </Typography>
                      </Box>
                    </Stack>
                    <Divider
                      sx={{
                        mb: 2,
                        borderColor: darkMode ? "grey.800" : "grey.300",
                      }}
                    />
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      color={"#CDD7E1"}
                      sx={{ mb: 0.5, fontSize: 16 }}
                    >
                      {log.title || "Log Details"}
                    </Typography>
                    <Box
                      sx={{
                        background: alpha("#CDD7E1", 0.08),
                        borderLeft: `3px solid ${"#CDD7E1"}`,
                        p: 2,
                        borderRadius: 2,
                        fontFamily: "JetBrains Mono, Fira Mono, monospace",
                        fontSize: 15,
                        color: theme.palette.text.primary,
                        boxShadow: "0 1px 8px 0 rgba(25,118,210,0.04)",
                        mb: 1,
                        whiteSpace: "pre-line",
                        overflowX: "auto",
                        transition: "background 0.4s, color 0.3s",
                      }}
                    >
                      {typeof log.details === "object"
                        ? JSON.stringify(log.details, null, 2)
                        : log.details}
                    </Box>
                  </Box>
                </Paper>
              </Slide>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}

export default ManageLogs;
